import { Ban, CheckCircle2, Cpu, PackageMinus, RotateCcw, ShieldAlert, Undo2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { usePermission } from '../../../../auth/AuthContext';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly, formatDateTime, toIso } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { ReturnStockUnitModal } from '../components/ReturnStockUnitModal';
import { StockUnitModal } from '../components/StockUnitModal';
import { WriteOffStockUnitModal } from '../components/WriteOffStockUnitModal';
import { personnelApi, personsApi } from '../../../human-resources/personnel/personnelApi';
import { LICENSE_PAYMENT_MODE_LABEL, OWNERSHIP_LABEL } from '../inventoryTypes';
import { stockUnitsApi } from '../stockApi';
import type { StockUnitDetail } from '../stockTypes';
import { UNIT_STATUS, UNIT_STATUS_LABEL, unitBlockedReason } from '../stockTypes';

type TabKey = 'info';

/**
 * One serialized thing.
 *
 * On the **header** layout: a unit reads as one continuous thing rather than a record whose
 * content splits into panels a reader chooses between.
 *
 * **Write off and Return to stock sit in the Unit card's cog, beside its Edit**, and both carry
 * `stock-units.write-off` — the same key in both directions, because deciding what is on the
 * owned tally is one privilege. They are cog VERBS rather than second Edits: the test is
 * whether a reader would describe the entry as "changing these details", and neither is that.
 */
export function StockUnitProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [unit, setUnit] = useState<StockUnitDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [writingOff, setWritingOff] = useState(false);
  const [returning, setReturning] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  /*
    The accountable employee's NAME, resolved for the ONE record on screen.

    It is two hops by design — the unit carries a `PersonnelId`, the personnel row carries a
    `PersonId`, and only the identity service turns that into a name. That is the whole reason
    no name is on the DTO: a list would mean resolving a page of them, and sorting one would
    mean resolving the table.

    Gated on `personnel.read`, which this page does not imply — a reader of stock units is not
    thereby a reader of personnel. Without it the card says `Hidden`, which is the honest
    statement: the seat has somebody accountable and this page was not given the name.
  */
  const canReadPersonnel = usePermission('personnel.read');
  const [accountableName, setAccountableName] = useState<string | null>(null);

  const accountableId = unit?.accountablePersonnelId ?? null;

  useEffect(() => {
    setAccountableName(null);

    if (accountableId === null || !canReadPersonnel) return;

    const controller = new AbortController();

    personnelApi
      .get(accountableId, controller.signal)
      .then(async (person) => {
        const [resolved] = await personsApi.resolve([person.personId], controller.signal);

        if (!controller.signal.aborted && resolved) setAccountableName(resolved.displayName);
      })
      // A failed resolve names no field and must not replace the page: the row stays as the
      // ellipsis it opened with, and everything else on the profile is still readable.
      .catch(() => undefined);

    return () => controller.abort();
  }, [accountableId, canReadPersonnel]);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      stockUnitsApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;

          setUnit(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this unit.', 0));
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  /**
   * Puts a deleted unit back.
   *
   * **No confirm tick and no password** — restoring puts a record back. It CAN fail, though,
   * and that is the difference from an item's restore: the serial index is filtered, so
   * deleting a unit frees its serial and another row of the same item may since have taken it.
   * The server re-checks and the failure lands in the banner.
   */
  async function restore() {
    if (!id) return;

    setRestoring(true);
    setError(null);

    try {
      await stockUnitsApi.activate(id);
      load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not restore this unit.', 0));
    } finally {
      setRestoring(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Logistics',
    'Inventory',
    { label: 'Stock Units', to: '/inventory/stock-units' },
    unit?.serialNumber ?? 'Unit',
  ];

  if (error && !unit) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (loading || !unit) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  /**
   * The banner, and every state renders.
   *
   * Amber is legal on a PROFILE — the no-yellow rule is about an index row, where the brand
   * colour reads as chrome the shell put there rather than as a fact about the record.
   */
  function banner() {
    if (error) {
      return { tone: 'red' as const, icon: <Ban className="h-4 w-4" aria-hidden="true" />, message: error.message };
    }

    if (!unit!.isActive) {
      return {
        tone: 'red' as const,
        icon: <Ban className="h-4 w-4" aria-hidden="true" />,
        message:
          'This unit is deleted. Its serial is FREE for another unit of the same item to take, so a restore is re-checked and can fail — unlike an item, whose SKU is never freed.',
      };
    }

    if (unit!.status === UNIT_STATUS.disposed) {
      return {
        tone: 'red' as const,
        icon: <Ban className="h-4 w-4" aria-hidden="true" />,
        message: unit!.statusReason
          ? `Disposed: ${unit!.statusReason}. A disposal is a certified destruction and is not undone here — if this turns up, the correction is a new record.`
          : 'Disposed. A disposal is a certified destruction and is not undone here.',
      };
    }

    if (unit!.status === UNIT_STATUS.stolen) {
      return {
        tone: 'red' as const,
        icon: <ShieldAlert className="h-4 w-4" aria-hidden="true" />,
        message: unit!.statusReason
          ? `Reported stolen: ${unit!.statusReason}. This one is reversible — if it turns up, Return to stock puts it back as an adjustment.`
          : 'Reported stolen. This is reversible: if it turns up, Return to stock puts it back.',
      };
    }

    if (!unit!.isOnHand) {
      return {
        tone: 'amber' as const,
        icon: <ShieldAlert className="h-4 w-4" aria-hidden="true" />,
        message: 'This unit is off the owned tally.',
      };
    }

    if (unit!.status !== UNIT_STATUS.inStock) {
      return {
        tone: 'amber' as const,
        icon: <ShieldAlert className="h-4 w-4" aria-hidden="true" />,
        message: unit!.statusReason
          ? `${UNIT_STATUS_LABEL[unit!.status]}: ${unit!.statusReason}. It is still HELD and still counts toward the owned tally — broken and present is not the same fact as gone.`
          : `${UNIT_STATUS_LABEL[unit!.status]}, and still held — it counts toward the owned tally.`,
      };
    }

    return {
      tone: 'slate' as const,
      icon: <CheckCircle2 className="h-4 w-4" aria-hidden="true" />,
      /*
        A unit that sits NOWHERE says so rather than naming a store — a licence seat is not at a
        facility, and interpolating a null would read "In stock at null". The rest of the
        sentence is the same fact either way: it counts toward the owned tally.
      */
      message:
        unit!.storageFacilityName === null
          ? `In stock and counting toward the owned tally for ${unit!.sku}. A licence is not held at a facility.`
          : `In stock at ${unit!.storageFacilityName}, and counting toward the owned tally for ${unit!.sku}.`,
    };
  }

  const state = banner();

  const unitActions = [
    {
      key: 'edit-unit',
      label: 'Edit',
      permission: 'stock-units.update',
      disabledReason: unit.isOnHand ? null : unitBlockedReason(unit.status, 'edit'),
      onClick: () => setEditing(true),
    },
    {
      key: 'write-off-unit',
      label: 'Write off',
      icon: <PackageMinus className="h-4 w-4" aria-hidden="true" />,
      permission: 'stock-units.write-off',
      disabledReason: unit.isOnHand ? null : unitBlockedReason(unit.status, 'write-off'),
      onClick: () => setWritingOff(true),
    },
    {
      key: 'return-unit',
      label: 'Return to stock',
      icon: <Undo2 className="h-4 w-4" aria-hidden="true" />,
      permission: 'stock-units.write-off',
      disabledReason:
        unit.status === UNIT_STATUS.stolen
          ? null
          : unit.status === UNIT_STATUS.disposed
            ? 'This unit was disposed of. A disposal is a certified destruction and is not undone here.'
            : 'Only a unit reported stolen can be returned to stock.',
      onClick: () => setReturning(true),
    },
  ];

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="stock-unit-profile"
        header={
          <ProfileHeader
            id="stock-unit-profile-header"
            icon={<Cpu className="h-6 w-6" aria-hidden="true" />}
            title={unit.serialNumber}
            meta={[unit.sku, unit.itemName, unit.storageFacilityName]}
            secondary={[
              UNIT_STATUS_LABEL[unit.status],
              unit.isOnHand ? 'On the owned tally' : 'Off the owned tally',
              unit.ownership === null ? 'Tenure from the item' : OWNERSHIP_LABEL[unit.ownership],
            ]}
            status={
              <StatusText tone={unit.isActive ? 'active' : 'inactive'}>
                {unit.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={unit.createdByName}
            createdAt={unit.createdAt}
          />
        }
        banner={
          <ProfileBanner
            id="stock-unit-profile-banner"
            tone={state.tone}
            icon={state.icon}
            message={state.message}
            action={
              !unit.isActive && !error ? (
                // Gates the button, not the banner: a read-only viewer still needs the state.
                <PermissionGate permission="stock-units.delete">
                  <Button
                    id="stock-unit-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void restore()}
                    disabled={restoring}
                    loading={restoring}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              ) : undefined
            }
          />
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard id="stock-unit-identity" title="Unit" menuItems={unitActions}>
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Serial number', value: unit.serialNumber, mono: true },
              {
                /*
                  BESIDE THE SERIAL, because both are identity — one the manufacturer's, one
                  ours. An ICS and a PAR print this one, and it is permanent: nothing on any
                  form can change it, and no request carries a field for it.

                  An em dash on a unit registered before the allocator existed. Those are
                  deliberately not backfilled: a property number records the moment a unit
                  entered the register, and minting one now would put a code on a shelf
                  nobody can find.
                */
                label: 'Property no.',
                value: unit.inventoryNumber,
                mono: unit.inventoryNumber !== null,
              },
              { label: 'Item', value: `${unit.sku} — ${unit.itemName}` },
              { label: 'Facility', value: unit.storageFacilityName },
              {
                label: 'Condition',
                value: (
                  <StatusText tone={unit.status === UNIT_STATUS.inStock ? 'active' : 'blocked'}>
                    {UNIT_STATUS_LABEL[unit.status]}
                  </StatusText>
                ),
              },
              {
                label: 'On the owned tally',
                // Damaged and under repair COUNT — broken and present is not the same fact as
                // gone, and this row is where that split is legible.
                value: unit.isOnHand ? 'Yes' : 'No',
              },
              { label: 'Reason', value: unit.statusReason },
              { label: 'Notes', value: unit.notes },
            ]}
          />
        </ProfileCard>

        <ProfileCard id="stock-unit-tenure" title="Tenure and value" menuItems={[unitActions[0]!]}>
          <PreviewTable
            variant="table-display"
            rows={[
              {
                label: 'Held as',
                // An em dash where the unit defers to the catalog item — "not stated here" is a
                // different fact from "owned".
                value: unit.ownership === null ? null : OWNERSHIP_LABEL[unit.ownership],
              },
              {
                label: 'Acquired',
                // CALENDAR dates throughout, never the instant path.
                value: unit.acquisitionDate === null ? null : formatDateOnly(unit.acquisitionDate),
                mono: true,
              },
              {
                // What the purchase was charged to, beside when it was made. The ICS and the
                // PAR print it in a column of their own.
                label: 'Fund',
                value: unit.fund,
              },
              {
                label: 'Lease ends',
                value: unit.leaseEndDate === null ? null : formatDateOnly(unit.leaseEndDate),
                mono: true,
              },
              {
                label: 'Term ends',
                value: unit.termEndDate === null ? null : formatDateOnly(unit.termEndDate),
                mono: true,
              },
              {
                label: 'Unit cost',
                value: unit.unitCost === null ? null : formatMoney(unit.unitCost),
                mono: true,
              },
            ]}
          />
        </ProfileCard>

        {/*
          THE LICENCE CARD, drawn only where the row actually carries one of its three values.

          Every one of them is optional and null on physical stock, so an always-drawn card
          would be three em dashes on every laptop in the register — and a column of em dashes
          says nothing the absence of the card does not say better. It is the one place the
          three fields are readable, which is what keeps them from being the value collected,
          stored and never read.

          It carries NO Edit, and that is a stated gap rather than an exemption: the three are
          written by the licence registration and there is no correction path for them yet. The
          unit's own edit form does not draw them, so a cog offering one would open a modal that
          cannot change what the card shows.
        */}
        {unit.paymentMode !== null ||
        unit.firstBillingDate !== null ||
        unit.accountablePersonnelId !== null ? (
          <ProfileCard id="stock-unit-licence" title="Licence">
            <PreviewTable
              variant="table-display"
              rows={[
                {
                  label: 'Accountable',
                  /*
                    A NAME, resolved for the ONE record on screen — never carried on the row.
                    `Hidden` rather than an em dash for a reader without `personnel.read`: the
                    seat HAS somebody accountable and this page has not been given the name,
                    which is a different fact from nobody being accountable.
                  */
                  value:
                    unit.accountablePersonnelId === null
                      ? null
                      : canReadPersonnel
                        ? (accountableName ?? '\u2026')
                        : 'Hidden',
                },
                {
                  label: 'Payment mode',
                  value:
                    unit.paymentMode === null
                      ? null
                      : LICENSE_PAYMENT_MODE_LABEL[unit.paymentMode],
                },
                {
                  label: 'First billing',
                  value:
                    unit.firstBillingDate === null
                      ? null
                      : formatDateOnly(unit.firstBillingDate),
                  mono: true,
                },
              ]}
            />
          </ProfileCard>
        ) : null}

        {/* Audit takes no Edit at any permission level: it records what happened. */}
        <ProfileCard id="stock-unit-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: unit.createdByName },
              {
                label: 'Created',
                value: <time dateTime={toIso(unit.createdAt)}>{formatDateTime(unit.createdAt)}</time>,
                mono: true,
              },
              { label: 'Updated by', value: unit.updatedByName },
              {
                label: 'Updated',
                value:
                  unit.updatedAt === null ? null : (
                    <time dateTime={toIso(unit.updatedAt)}>{formatDateTime(unit.updatedAt)}</time>
                  ),
                mono: true,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <StockUnitModal
          open
          editing={unit}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}

      {writingOff ? (
        <WriteOffStockUnitModal
          open
          unit={unit}
          onClose={() => setWritingOff(false)}
          onSaved={() => {
            setWritingOff(false);
            load();
          }}
        />
      ) : null}

      {returning ? (
        <ReturnStockUnitModal
          open
          unit={unit}
          onClose={() => setReturning(false)}
          onSaved={() => {
            setReturning(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
